import React from 'react';
import LinearProgress from '@material-ui/core/LinearProgress';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core';
import { useNumberFormatter } from '../../../../../hooks/useNumberFormatter';

const useStyles = makeStyles({
    barColorPrimary: {
        backgroundColor: '#236BFF',
        borderRadius: 8,
    },
    colorPrimary: {
        backgroundColor: '#D0D5E0',
        borderRadius: 8,
    },
});

function clamp(n, min, max) {
    return Math.min(Math.max(Number(n) || 0, min), max);
}

interface lineProps {
    totalDisbursed: number
    totalPending: number;
    totalDisbursedPercent: number;
    totalPendingPercent: number;
    format?: string;
}

const LineProgress: React.FC<lineProps> = ({
    totalDisbursed = 0,
    totalPending = 0,
    totalDisbursedPercent,
    totalPendingPercent,
    format
}) => {
    const { t } = useTranslation();
    const { formatMillions } = useNumberFormatter();
    const classes = useStyles();

    let disbursedPct = 0;
    let pendingPct = 0;

    const hasDisbursedPct = typeof totalDisbursedPercent === 'number';
    const hasPendingPct = typeof totalPendingPercent === 'number';

    if (hasDisbursedPct && hasPendingPct) {
        disbursedPct = clamp(totalDisbursedPercent, 0, 100);
        pendingPct = clamp(totalPendingPercent, 0, 100);
    } else if (hasDisbursedPct) {
        disbursedPct = clamp(totalDisbursedPercent, 0, 100);
        pendingPct = clamp(100 - disbursedPct, 0, 100);
    } else if (hasPendingPct) {
        pendingPct = clamp(totalPendingPercent, 0, 100);
        disbursedPct = clamp(100 - pendingPct, 0, 100);
    } else {
        const disb = Number(totalDisbursed) || 0;
        const pend = Number(totalPending) || 0;
        const total = disb + pend;
        if (total > 0) {
            disbursedPct = clamp((disb / total) * 100, 0, 100);
            pendingPct = clamp(100 - disbursedPct, 0, 100);
        } else {
            disbursedPct = 0;
            pendingPct = 0;
        }
    }

    return (
        <div className="c-progress-bar">
            <LinearProgress
                className="linear-chart"
                variant="determinate"
                value={disbursedPct}
                style={{ width: '100%' }}
                classes={{
                    barColorPrimary: classes.barColorPrimary,
                    colorPrimary: classes.colorPrimary,
                }}
            />

            <div className="c-linear_porcent-data">
                <div className="content-data">
                    <p className="initial-status-text">{t('one_pager_report.disbursed')}</p>
                    <div className="initial-data-content">
                        <div className="percent">{disbursedPct.toFixed(0)} %</div>
                        <div className="amount">{formatMillions(totalDisbursed)} MM {format}</div>
                    </div>
                </div>

                <div className="content-data">
                    <p className="pending-status-text">{t('one_pager_report.pending')}</p>
                    <div className="pending-data-content">
                        <div className="percent">{pendingPct.toFixed(0)} %</div>
                        <div className="amount">{formatMillions(totalPending)} MM {format}</div>
                    </div>
                </div>
            </div>
        </div>
    );
}


export default LineProgress;
